import { MAX_MESSAGES } from "@/pages/campaign-form/components/campaigns/writeALetter/Message"
import { createOfficialsSafedSearch } from "@/pages/campaign-form/helpers"
import { CampaignFormValues } from "@/pages/campaign-form/types"
import { CampaignMessageGroup } from "@/types/djangio"
import { UseFieldArrayAppend, UseFieldArrayRemove, UseFieldArrayUpdate, UseFormReturn } from "react-hook-form"
import { isInternational } from "shared/imports/regionConstants"

export const getKebabMenuOptions = (
    messageId: number,
    messages: CampaignMessageGroup[],
    formContext: UseFormReturn<CampaignFormValues>,
    update: UseFieldArrayUpdate<CampaignFormValues, "message_groups">,
    remove: UseFieldArrayRemove,
    append: UseFieldArrayAppend<CampaignFormValues, "message_groups">,
    setOpened: (path: string) => void,
    prefix: string = null,
) => {
    const messageGroupsName = `${prefix ? prefix + "." : ""}message_groups`

    const options = [
        {
            text: "Duplicate",
            icon: "copy",
            onClick: () => {
                const message = formContext.getValues(messageGroupsName)[messageId]
                const newMessage = {
                    ...message,
                    standard_options_search: null,
                    custom_officials_searches: null,
                    custom_email_targets: [],
                    id: null,
                    name: `${message.name} (copy)`,
                    message_variations: message.message_variations.map((variation) => ({
                        ...variation,
                        id: null,
                    })),
                }
                append(newMessage)
                setOpened(`${messages.length + 1}`)
                createOfficialsSafedSearch(formContext, messageId + 1, prefix)
            },
            disabled: messages.length === MAX_MESSAGES,
        },
        {
            text: "Clear content",
            icon: "eraser",
            onClick: () => {
                const message = formContext.getValues(messageGroupsName)[messageId]
                const archivedMessageVariations = message?.message_variations
                    .map((variation) => {
                        if (variation.id) {
                            return {
                                ...variation,
                                archived: true,
                            }
                        }
                        return null
                    })
                    .filter((variation) => variation !== null)

                const newMessage = {
                    ...message,
                    standard_options_search: null,
                    custom_officials_searches: null,
                    custom_email_targets: [],
                    uses_default_member_list: !isInternational,
                    draft_requirements: DjangIO.app.grassroots.campaign.types.DraftOptions.can_edit.value,
                    message_variations_type: DjangIO.app.grassroots.campaign.types.MessageVariationsType.none.value,
                    message_variations: [
                        {
                            subject: "",
                            content: "",
                            archived: false,
                            fixed_pre_content: "",
                            fixed_post_content: "",
                            index: 0,
                        },
                    ],
                    has_custom_email_targets: false,
                    archived_variations: archivedMessageVariations,
                }
                update(messageId, newMessage)
            },
        },
    ]

    if (messages.length > 1) {
        options.push({
            text: "Delete",
            icon: "trash",
            onClick: () => {
                const message = formContext.getValues(messageGroupsName)[messageId]

                if (!message.id) {
                    remove(messageId)
                    return
                }

                update(messageId, {
                    ...message,
                    archived: true,
                })
            },
        })
    }

    return options
}

export const getMessageFieldName = (messageId: number, field: string, prefix: string = null) =>
    `${prefix ? prefix + "." : ""}message_groups[${messageId}].${field}`

export const getMessageVariationFieldName = (
    messageId: number,
    variationId: number,
    field: string,
    prefix: string = null,
) => `${prefix ? prefix + "." : ""}message_groups[${messageId}].message_variations[${variationId}].${field}`
